import React from 'react';
import { useTranslation } from 'react-i18next';

type LogoProps = {
    className?: string;
    variant?: 'color' | 'white' | 'icon';
    size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl'; // Added 2xl
    showSlogan?: boolean;
    orientation?: 'horizontal' | 'vertical';
    hideTextOnMobile?: boolean;
};

export const Logo: React.FC<LogoProps> = ({
    className = '',
    variant = 'color',
    size = 'md',
    showSlogan = false,
    orientation = 'horizontal',
    hideTextOnMobile = false
}) => {
    const { i18n } = useTranslation();
    const currentLang = i18n.language || 'fr';

    // Slogans dictionary
    const slogans: Record<string, string> = {
        fr: "Services de proximité en un clic",
        en: "Local services nearby",
        ar: "خدمات قريبة منك"
    };

    // Sizes logic
    const sizeClasses = {
        sm: { h: 'h-8', text: 'text-lg', slogan: 'text-xs' },
        md: { h: 'h-10', text: 'text-2xl', slogan: 'text-xs' },
        lg: { h: 'h-14', text: 'text-3xl', slogan: 'text-xs' },
        xl: { h: 'h-20', text: 'text-4xl', slogan: 'text-sm' },
        '2xl': { h: 'h-32', text: 'text-6xl', slogan: 'text-lg' },
    };

    const currentSize = sizeClasses[size];

    // Icon Only Variant
    if (variant === 'icon') {
        return (
            <img
                src="/logo-96.webp"
                alt="Bricolat"
                className={`${currentSize.h} w-auto ${className}`}
              loading="lazy"/>
        );
    }

    const textColor = variant === 'white' ? 'text-white' : 'text-gray-900';
    const sloganColor = variant === 'white' ? 'text-gray-200' : 'text-gray-500';

    return (
        <div className={`flex ${orientation === 'vertical' ? 'flex-col justify-center' : 'flex-row'} items-center gap-2 ${className}`}>
            <img
                src="/logo-96.webp"
                // Decorative when the brand name is written next to it (a repeated alt is read twice);
                // it keeps its name when the text is hidden on mobile.
                alt={hideTextOnMobile ? 'Bricolat' : ''}
                className={`${currentSize.h} w-auto object-contain`}
              loading="lazy"/>
            <div className={`flex flex-col ${orientation === 'vertical' ? 'items-center' : 'items-start'} ${hideTextOnMobile ? 'hidden md:flex' : ''}`}>
                <span className={`font-bold tracking-tight uppercase leading-none ${currentSize.text} ${textColor}`}>
                    Bricolat
                </span>
                {showSlogan && (
                    <span className={`font-medium tracking-wide ${currentSize.slogan} ${sloganColor} ${currentLang === 'ar' ? 'font-arabic' : ''}`}
                        style={{ fontFamily: currentLang === 'ar' ? 'sans-serif' : 'inherit' }}>
                        {slogans[currentLang]}
                    </span>
                )}
            </div>
        </div>
    );
};
